import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Pencil, Trash2, Wand2 } from 'lucide-react';
import { OrgPortalApi } from '@/services/org.api';
import { OrgDepartmentRow, OrgReportsApi } from '@/services/org-reports.api';
import { OrgDepartmentsApi } from '@/services/org-departments.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Select, Input, Textarea } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';

function errMsg(e: unknown): string | undefined {
  if (!e) return undefined;
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong';
}

interface DeptForm {
  id: string | null;
  schoolId: string;
  name: string;
  description: string;
  subjectIds: string[];
}

const BLANK_FORM: DeptForm = { id: null, schoolId: '', name: '', description: '', subjectIds: [] };

/**
 * Departments — org tier. Real legacy's Departments screen is genuinely
 * Super-Admin-only (confirmed via live permission grants — only the
 * "Super Admin" role holds any `department-*` permission), with an explicit
 * "School *" field on the create/edit form rather than an "enter a school"
 * redirect, a real many-to-many Subjects link (`department_subjects`), and
 * an "Auto Generate from Subjects" bulk action. See
 * backend/src/modules/org-departments for the write endpoints this page
 * calls; listing stays on the existing cross-school report endpoint.
 */
export function OrgDepartmentsPage() {
  const [schoolId, setSchoolId] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [form, setForm] = useState<DeptForm>(BLANK_FORM);
  const [formError, setFormError] = useState<string | undefined>();
  const [generateMsg, setGenerateMsg] = useState<string | undefined>();
  const [deleting, setDeleting] = useState<OrgDepartmentRow | null>(null);
  const [deleteError, setDeleteError] = useState<string | undefined>();
  const queryClient = useQueryClient();

  const { data: schools = [] } = useQuery({
    queryKey: ['schools-for-departments-report'],
    queryFn: async () => (await OrgPortalApi.listSchools({ limit: 100 })).items,
  });

  const { data: rows = [], isFetching } = useQuery({
    queryKey: ['org-departments', schoolId],
    queryFn: () => OrgReportsApi.departments({ schoolId: schoolId || undefined }),
  });

  const { data: formSubjects = [] } = useQuery({
    queryKey: ['org-department-subjects', form.schoolId],
    queryFn: () => OrgDepartmentsApi.subjects(form.schoolId),
    enabled: !!form.schoolId,
  });

  // Picking a different school for a brand-new department clears any
  // subject selection made against the previous school's subject list.
  useEffect(() => {
    if (!form.id) setForm((f) => ({ ...f, subjectIds: [] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.schoolId]);

  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        schoolId: form.schoolId,
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        subjectIds: form.subjectIds,
      };
      return form.id ? OrgDepartmentsApi.update(form.id, payload) : OrgDepartmentsApi.create(payload);
    },
    onSuccess: () => {
      setForm(BLANK_FORM);
      setFormError(undefined);
      queryClient.invalidateQueries({ queryKey: ['org-departments'] });
    },
    onError: (e) => setFormError(errMsg(e)),
  });

  const generate = useMutation({
    mutationFn: () => OrgDepartmentsApi.generate(form.schoolId),
    onSuccess: (r) => {
      setGenerateMsg(`Created ${r.created} department(s) from subjects without one yet.`);
      queryClient.invalidateQueries({ queryKey: ['org-departments'] });
    },
    onError: (e) => setGenerateMsg(errMsg(e)),
  });

  const removeDept = useMutation({
    mutationFn: () => OrgDepartmentsApi.remove(deleting!.departmentId, deleting!.schoolId),
    onSuccess: () => {
      setDeleting(null);
      setDeleteError(undefined);
      queryClient.invalidateQueries({ queryKey: ['org-departments'] });
    },
    onError: (e) => setDeleteError(errMsg(e)),
  });

  const startEdit = (r: OrgDepartmentRow) => {
    setFormError(undefined);
    setGenerateMsg(undefined);
    setForm({
      id: r.departmentId,
      schoolId: r.schoolId,
      name: r.departmentName,
      description: r.description ?? '',
      subjectIds: r.subjectIds,
    });
  };

  return (
    <>
      <PageHeader
        title="Departments"
        description="Create, edit, and auto-generate departments for any school in your organization."
      />

      <div className="mb-6 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <h3 className="mb-3 text-sm font-semibold text-slate-800">
          {form.id ? 'Edit Department' : 'Create Department'}
        </h3>
        {formError && (
          <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{formError}</div>
        )}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
          className="grid grid-cols-1 gap-4 sm:grid-cols-2"
        >
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">
              Name <span className="text-red-500">*</span>
            </label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Name"
              required
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Description</label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={1}
              placeholder="Description"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Subjects</label>
            <div className="max-h-36 overflow-y-auto rounded-md border border-slate-300 p-2">
              {!form.schoolId ? (
                <p className="text-xs text-slate-400">Pick a school to choose subjects.</p>
              ) : formSubjects.length === 0 ? (
                <p className="text-xs text-slate-400">No subjects in this school yet.</p>
              ) : (
                formSubjects.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 py-0.5 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={form.subjectIds.includes(s.id)}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          subjectIds: e.target.checked
                            ? [...form.subjectIds, s.id]
                            : form.subjectIds.filter((id) => id !== s.id),
                        })
                      }
                    />
                    {s.name}
                  </label>
                ))
              )}
            </div>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">
              School <span className="text-red-500">*</span>
            </label>
            <Select
              className="!w-full"
              value={form.schoolId}
              onChange={(e) => setForm({ ...form, schoolId: e.target.value })}
              disabled={!!form.id}
              required
            >
              <option value="">Select School</option>
              {schools.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          </div>

          <div className="col-span-full flex flex-wrap items-center gap-2 pt-1">
            <button type="submit" className="btn-primary" disabled={!form.name.trim() || !form.schoolId || save.isPending}>
              {save.isPending ? 'Saving…' : form.id ? 'Update' : 'Submit'}
            </button>
            {!form.id && (
              <button
                type="button"
                className="btn-secondary flex items-center gap-1.5"
                disabled={!form.schoolId || generate.isPending}
                onClick={() => {
                  setGenerateMsg(undefined);
                  generate.mutate();
                }}
              >
                <Wand2 className="h-4 w-4" /> {generate.isPending ? 'Generating…' : 'Auto Generate from Subjects'}
              </button>
            )}
            {form.id && (
              <button type="button" className="btn-secondary" onClick={() => setForm(BLANK_FORM)}>
                Cancel
              </button>
            )}
            {generateMsg && <span className="text-sm text-slate-600">{generateMsg}</span>}
          </div>
        </form>
      </div>

      <h3 className="mb-2 text-sm font-semibold text-slate-800">List Departments</h3>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Select className="!w-56" value={schoolId} onChange={(e) => setSchoolId(e.target.value)}>
          <option value="">All schools</option>
          {schools.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500"></th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">School</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">Department</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">Description</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">Subjects</th>
              <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wide text-slate-500">Total Subjects</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">Type</th>
              <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wide text-slate-500">Teachers</th>
              <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wide text-slate-500">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isFetching ? (
              <tr>
                <td colSpan={9} className="px-4 py-6 text-center text-sm text-slate-400">
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-6 text-center text-sm text-slate-400">
                  No departments found.
                </td>
              </tr>
            ) : (
              rows.map((r: OrgDepartmentRow) => {
                const key = r.departmentId;
                const isOpen = expanded.has(key);
                return (
                  <>
                    <tr
                      key={key}
                      className={r.teacherCount > 0 ? 'cursor-pointer hover:bg-slate-50' : ''}
                      onClick={() => r.teacherCount > 0 && toggle(key)}
                    >
                      <td className="px-4 py-2.5 text-slate-400">
                        {r.teacherCount > 0 ? isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" /> : null}
                      </td>
                      <td className="px-4 py-2.5 text-sm font-medium text-slate-800">{r.schoolName}</td>
                      <td className="px-4 py-2.5 text-sm text-slate-700">{r.departmentName}</td>
                      <td className="px-4 py-2.5 text-sm text-slate-500">{r.description ?? '—'}</td>
                      <td className="px-4 py-2.5 text-sm text-slate-500">
                        {r.subjectNames.length ? r.subjectNames.join(', ') : '—'}
                      </td>
                      <td className="px-4 py-2.5 text-right text-sm text-slate-700">{r.subjectNames.length}</td>
                      <td className="px-4 py-2.5">
                        <Badge tone={r.autoGenerated ? 'blue' : 'slate'}>{r.autoGenerated ? 'Auto' : 'Manual'}</Badge>
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <Badge tone={r.teacherCount > 0 ? 'blue' : 'slate'}>{r.teacherCount}</Badge>
                      </td>
                      <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            title="Edit"
                            className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                            onClick={() => startEdit(r)}
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            title="Delete"
                            className="rounded p-1 text-slate-500 hover:bg-red-50 hover:text-red-600"
                            onClick={() => {
                              setDeleteError(undefined);
                              setDeleting(r);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {isOpen && r.teacherCount > 0 && (
                      <tr key={`${key}-teachers`}>
                        <td colSpan={9} className="bg-slate-50 px-4 py-2.5 text-sm text-slate-600">
                          <span className="text-xs font-medium uppercase tracking-wide text-slate-400">Teachers: </span>
                          {r.teacherNames.join(', ')}
                        </td>
                      </tr>
                    )}
                  </>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => removeDept.mutate()}
        title="Delete department?"
        message={deleteError ?? `This will permanently delete "${deleting?.departmentName}". This cannot be undone.`}
        confirmText="Delete"
        destructive
        loading={removeDept.isPending}
      />
    </>
  );
}
